type ParamSource = URL | URLSearchParams | string | null | undefined;

function getSearchParams(source: ParamSource): URLSearchParams {
  if (typeof source === "string") {
    return new URL(source, "https://join.local").searchParams;
  }

  if (source instanceof URL) {
    return source.searchParams;
  }

  return source ?? new URLSearchParams();
}

export function getOnlineJoinAsOfDate(source: ParamSource): string | null {
  const searchParams = getSearchParams(source);
  const rawValue = searchParams.get("asOf") ?? searchParams.get("date");
  if (!rawValue) return null;

  const value = rawValue.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const isoDatePrefix = value.match(/^(\d{4}-\d{2}-\d{2})T/);
  return isoDatePrefix?.[1] ?? null;
}

export function getOnlineJoinTestParams(source: ParamSource): URLSearchParams {
  const searchParams = getSearchParams(source);
  const result = new URLSearchParams();

  const asOfDate = getOnlineJoinAsOfDate(searchParams);
  if (asOfDate) result.set("asOf", asOfDate);

  const period = searchParams.get("period")?.trim();
  if (period) result.set("period", period);

  // Member-entered promo code: forwarded to the plans API (?promo=) so a code-gated campaign set is
  // served, and carried through the funnel steps exactly like asOf/period so add-on resolution and
  // checkout re-validations price against the SAME set the member saw.
  const promo = searchParams.get("promo")?.trim();
  if (promo) result.set("promo", promo);

  // Admin Preview Live links version the public cache after a plan-set change. Carry it through every funnel step.
  const previewRevision = searchParams.get("previewRev")?.trim();
  if (previewRevision && previewRevision.length <= 128) result.set("previewRev", previewRevision);

  const debug = searchParams.get("debug")?.trim().toLowerCase();
  if (debug === "true") result.set("debug", "true");

  return result;
}

export function appendOnlineJoinTestParams(path: string, source: ParamSource): string {
  const params = getOnlineJoinTestParams(source);
  const query = params.toString();
  if (!query) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${query}`;
}

/** The ONLY list of funnel-carried params. Route actions must use getOnlineJoinTestParamsFromFormData
 * instead of hand-rolling this list — six routes silently dropped "promo" by re-typing it. */
export const ONLINE_JOIN_TEST_PARAM_KEYS = ["asOf", "period", "promo", "previewRev", "debug"] as const;

export function getOnlineJoinTestParamsFromFormData(formData: FormData): URLSearchParams {
  const source = new URLSearchParams();
  for (const key of ONLINE_JOIN_TEST_PARAM_KEYS) {
    const value = formData.get(key)?.toString();
    if (value) source.set(key, value);
  }
  return getOnlineJoinTestParams(source);
}

export function appendOnlineJoinTestParamsToFormData(formData: FormData, source: ParamSource) {
  const params = getOnlineJoinTestParams(source);
  for (const [key, value] of params.entries()) {
    formData.set(key, value);
  }
}
